import { useQuery, keepPreviousData } from '@tanstack/react-query';
import axios from 'axios';
import { toast } from 'sonner';
import { setCache } from '../lib/appCache';
import { queryClient } from '../lib/queryClient';

const API = '/api';

/**
 * Custom hook: show IndexedDB data instantly, then refresh from server.
 * No skeleton ever for cached data.
 */
function useCachedQuery(key, queryFn, options = {}) {
  const cacheKey = Array.isArray(key) ? key.join(':') : key;
  // NOTE: removed the async useEffect seeding from IndexedDB — it was causing
  // a tight refetch loop via internal React Query scheduler when the cached
  // data race-conditioned with the live query. Initial seeding is now handled
  // once at app bootstrap in App.js.

  return useQuery({
    queryKey: key,
    queryFn: async () => {
      const data = await queryFn();
      // Save to IndexedDB for next time (keep backward-compatible format)
      setCache(cacheKey, data);
      return data;
    },
    ...options,
  });
}

export const useMatches = (date) => {
  return useCachedQuery(
    ['matches', date],
    async () => {
      const { data } = await axios.get(`${API}/matches?date=${date}`, { timeout: 30000 });
      return data;
    },
    {
      staleTime: 10 * 1000,
      retry: 3,
      retryDelay: 3000,
      // iter262 (2026-10-06): the `/matches` endpoint is HEAVY (fetches
      // 100-1000+ fixtures with scores+odds+status). Polling it every 15s
      // was causing a 30-60s lag on live scores because the backend
      // serves stale-while-revalidate and the revalidation itself takes
      // 5-10s. We now rely on `useLiveScores()` for sub-15s live updates
      // and only refetch the full `/matches` every 90s to catch new
      // fixtures or final whistles that flip `is_live` from true→false.
      refetchInterval: (query) => {
        const d = query?.state?.data;
        const list = Array.isArray(d) ? d : (Array.isArray(d?.matches) ? d.matches : []);
        const hasLive = list.some(m => m && m.is_live);
        return hasLive ? 90 * 1000 : 120 * 1000;
      },
      refetchIntervalInBackground: false,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
    }
  );
};

/**
 * iter262 (2026-10-06) — Fast live-score sync.
 *
 * Polls the dedicated `/matches/live-scores` endpoint every 10s (which
 * backend caches 8s against API-Football). Returns a Map keyed by
 * fixture_id with the latest `{status_short, elapsed, home_score,
 * away_score, home_red, away_red}`.
 *
 * The Dashboard merges this map into the useMatches payload so live
 * scores on the Home list update at ~diretta.it cadence (~10-15s)
 * without re-fetching the heavy full-day listing.
 */
export const useLiveScores = (enabled = true) => {
  return useQuery({
    queryKey: ['live-scores'],
    queryFn: async () => {
      const { data } = await axios.get(`${API}/matches/live-scores`, { timeout: 10000 });
      // Convert to map for O(1) lookup during render
      const map = {};
      (data?.scores || []).forEach(s => {
        if (s.fixture_id != null) map[s.fixture_id] = s;
      });
      return { count: data?.count || 0, byFixtureId: map };
    },
    enabled,
    staleTime: 5 * 1000,
    retry: 1,
    retryDelay: 2000,
    // Pause when 0 live matches. The Home list already knows via
    // useMatches whether there's live traffic; passing `enabled`
    // from the consumer flips this off outside live windows.
    refetchInterval: 5 * 1000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });
};

export const useConversations = (enabled = true) => {
  const cacheKey = 'conversations';

  // NOTE: removed the async useEffect that did `queryClient.setQueryData` here.
  // It was racing with React Query's internal `run` scheduler and triggering
  // a tight refetch loop (~83 req/s). IndexedDB seeding now happens once at
  // app bootstrap in App.js (before any consumer mounts).

  // Track consecutive failures so we warn the admin exactly once
  // (prevents the silent "stuck at 15:00" symptom)
  return useQuery({
    queryKey: ['conversations'],
    queryFn: async () => {
      const startedAt = Date.now();
      try {
        // Cache-buster query param defeats any intermediate HTTP proxy cache
        // (CloudFront/nginx/service-worker) that might serve stale responses.
        // We fetch the FIRST page (limit=50). Older conversations are loaded
        // via useMoreConversations / loadMoreConversations infinite scroll.
        const { data } = await axios.get(
          `${API}/chat/conversations?skip=0&limit=50&_t=${Date.now()}`,
          { timeout: 30000 }
        );
        const convs = data.conversations || data;
        const elapsed = Date.now() - startedAt;
        // Diagnostic log (visible in Safari/Chrome remote debugger)
        if (elapsed > 3000) {
          console.warn(`[RVINCENTE] /conversations slow: ${elapsed}ms server_reported=${data.query_ms}ms total=${data.total}`);
        } else {
          console.log(`[RVINCENTE] /conversations OK: ${elapsed}ms count=${convs.length} has_more=${data.has_more}`);
        }
        setCache(cacheKey, convs);
        // Clear any lingering error toast on success
        try { window.__rvincente_conv_error_shown = false; } catch {}
        // Preserve has_more flag on query meta for infinite-scroll logic
        try {
          queryClient.setQueryData(['conversations-meta'], {
            has_more: !!data.has_more,
            server_time: data.server_time,
            total: data.total,
          });
        } catch {}
        return convs;
      } catch (err) {
        const elapsed = Date.now() - startedAt;
        const status = err?.response?.status;
        const code = err?.code;
        console.error(`[RVINCENTE] /conversations FAIL (${elapsed}ms) status=${status} code=${code}`, err?.message);
        // NO TOAST for transient server errors (5xx) or network issues —
        // React Query + placeholderData keeps the list visible.
        // We only surface a small inline indicator via window flag (consumed by ChatPage).
        try {
          if (status >= 500 || code === 'ECONNABORTED' || code === 'ERR_NETWORK' || !status) {
            window.__rvincente_conn_warning = Date.now();
          } else if (status === 401) {
            // 401 is an auth problem — users need to know
            if (!window.__rvincente_conv_error_shown) {
              window.__rvincente_conv_error_shown = true;
              toast.error('Sessione scaduta. Effettua di nuovo il login.', { duration: 5000 });
              setTimeout(() => { try { window.__rvincente_conv_error_shown = false; } catch {} }, 15000);
            }
          }
        } catch {}
        throw err;
      }
    },
    staleTime: 60 * 1000,
    // NO polling refetchInterval: delta sync is handled via WebSocket new_message
    // events + appResume listener. Polling every 15s caused a request storm when
    // multiple admins were online (N admins × 4 req/min × M conv subs).
    refetchIntervalInBackground: false,
    refetchOnMount: false,       // IndexedDB seed is enough; avoid re-fetch on every mount
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    retry: 3,                  // 3 attempts before surfacing error
    retryDelay: (attempt) => Math.min(1000 * Math.pow(2, attempt), 8000), // 1s, 2s, 4s, 8s
    placeholderData: keepPreviousData,  // never wipe list during refetch/error
    enabled,
  });
};

export const useVIPPredictions = (enabled = true) => {
  return useCachedQuery(
    ['vip-predictions'],
    async () => {
      const { data } = await axios.get(`${API}/vip-predictions`);
      return data;
    },
    {
      // Show cached data instantly, poll in background for freshness.
      // placeholderData: keepPreviousData → durante il refetch NON resetta
      // a undefined; lista rimane visibile invece di mostrare skeleton.
      staleTime: 10 * 1000,
      refetchInterval: 30 * 1000,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      placeholderData: keepPreviousData,
      enabled,
    }
  );
};

export const useVictoryGallery = () => {
  return useCachedQuery(
    ['victory-gallery'],
    async () => {
      const { data } = await axios.get(`${API}/victory-gallery`);
      return data;
    },
    {
      // 2026-02: users reported "vittoria appena caricata non appare
      // subito, appare dopo tempo". Root cause: staleTime=2min meant
      // React Query never refetched on VIP page reopen unless 2min
      // passed. Fix: staleTime=0 + refetchOnMount=always so every open
      // fetches fresh. Backend caches 5min so DB cost stays flat.
      staleTime: 0,
      refetchOnMount: 'always',
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      // Belt-and-suspenders: refresh every 90s while VIP page is open
      // so users who never close the app still see new victories
      // shortly after publication.
      refetchInterval: 90 * 1000,
      refetchIntervalInBackground: false,
      placeholderData: keepPreviousData,
    }
  );
};

export const useBonuses = () => {
  return useCachedQuery(
    ['bonuses'],
    async () => {
      const { data } = await axios.get(`${API}/bonuses`);
      return data;
    },
    {
      // Always-fresh behaviour: every time the user opens the Bonus tab or
      // brings the app to foreground we re-fetch from server. Backend has its
      // own 5-min cache so the DB cost stays flat regardless of how many
      // clients refetch.
      staleTime: 0,
      retry: 0,
      refetchOnMount: 'always',
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      // Belt-and-suspenders: refresh every 5 min while the tab is open in
      // foreground so a user who never closes the app still picks up admin
      // changes within minutes. Disabled in background to save battery.
      refetchInterval: 5 * 60 * 1000,
      refetchIntervalInBackground: false,
    }
  );
};

export const useMatchDetail = (fixtureId) => {
  return useCachedQuery(
    ['match-detail', fixtureId],
    async () => {
      const { data } = await axios.get(`${API}/matches/${fixtureId}`);
      return data;
    },
    { staleTime: 2 * 60 * 1000, enabled: !!fixtureId }
  );
};

export const usePrediction = (fixtureId, isGuest = false, enabled = true) => {
  return useCachedQuery(
    ['prediction', fixtureId, isGuest],
    async () => {
      const url = isGuest ? `${API}/predictions/${fixtureId}/guest` : `${API}/predictions/${fixtureId}`;
      const { data } = await axios.get(url, { timeout: 60000 });
      return data;
    },
    { staleTime: 5 * 60 * 1000, enabled: enabled && !!fixtureId, retry: 0 }
  );
};

export const useMatchLineups = (fixtureId, enabled = true) => {
  return useCachedQuery(
    ['match-lineups', fixtureId],
    async () => {
      const { data } = await axios.get(`${API}/matches/${fixtureId}/lineups`);
      return data.lineups || [];
    },
    {
      // Short staleTime + focus refetch so, mano a mano che il kickoff
      // si avvicina, le formazioni provvisorie DIY vengono sostituite
      // dalle ufficiali di API-Football non appena pubblicate
      // (~30-60 min pre-partita).
      staleTime: 60 * 1000,
      refetchOnWindowFocus: true,
      refetchOnMount: true,
      enabled: enabled && !!fixtureId,
    }
  );
};

